import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { initializeApp, getApps } from 'firebase/app';
import Sidebar from './components/Sidebar';
import BrandLogo from './components/BrandLogo';
import TopBar from './components/TopBar';
import { DateRangeProvider } from './context/DateRangeContext';
import { api } from './services/api';
import { ArrowRight, CheckCircle2, ShieldCheck } from 'lucide-react';

const LOCAL_QA_SESSION_KEY = 'localAdminQaSession';
const LOCAL_QA_LOGIN_ENABLED = process.env.REACT_APP_ENABLE_LOCAL_QA_LOGIN === 'true'
    && ['localhost', '127.0.0.1'].includes(window.location.hostname);

// Lazy load route components
const Dashboard = lazy(() => import('./components/Dashboard'));
const Library = lazy(() => import('./components/Library'));
const PaymentsInfo = lazy(() => import('./components/PaymentsInfo'));
const PaymentReceipt = lazy(() => import('./components/PaymentReceipt'));
const TeamManagement = lazy(() => import('./components/TeamManagement'));
const ActivityLogs = lazy(() => import('./components/ActivityLogs'));
const DeleteRequests = lazy(() => import('./components/DeleteRequests'));
const Reports = lazy(() => import('./components/Reports'));
const GeneralSettings = lazy(() => import('./components/GeneralSettings'));

const firebaseConfig = {
   apiKey: "AIzaSyBhyV-7A75rkHQ1ouDBtMWWn1PWFsIX1Fg",
  authDomain: "libmate-signin.firebaseapp.com",
  projectId: "libmate-signin",
  storageBucket: "libmate-signin.firebasestorage.app",
  messagingSenderId: "764163771306",
  appId: "1:764163771306:web:9eaf0ef8c6fa992643b9d3",
  measurementId: "G-SFQQQVXL4X"
};

if (!getApps().length) {
    initializeApp(firebaseConfig);
}

const LoadingSpinner = () => (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
    </div>
);

const PageLoader = () => (
    <div className="flex-1 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
    </div>
);

const AdminSignIn = ({ onSignIn, onLocalSignIn }) => {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const auth = getAuth();
    const provider = new GoogleAuthProvider();

    const handleGoogleSignIn = async () => {
        setError('');
        setLoading(true);
        try {
            const result = await signInWithPopup(auth, provider);
            await onSignIn(result.user);
        } catch (error) {
            console.error('Sign-in error:', error);
            const msg = error.response?.data?.error || error.message || 'Sign-in failed';
            setError(msg.replace('Firebase: ', ''));
            setLoading(false);
        }
    };

    const handleLocalSignIn = async () => {
        setError('');
        setLoading(true);
        try {
            await onLocalSignIn();
        } catch (error) {
            console.error('Local QA sign-in error:', error);
            setError(error.message || 'Local QA sign-in failed');
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#f5f7fa] lg:grid lg:grid-cols-[1.08fr_0.92fr]">
            <section className="hidden lg:flex relative overflow-hidden bg-indigo-950 px-14 py-12 text-white">
                <div className="absolute -left-24 top-1/3 h-80 w-80 rounded-full bg-blue-600/20 blur-3xl" />
                <div className="absolute -right-28 -top-24 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />
                <div className="relative z-10 flex max-w-xl flex-col justify-between">
                    <div><BrandLogo width={220} height={70} lightBackground /><p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-indigo-300">Platform operations</p></div>
                    <div className="py-14">
                        <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold text-indigo-100">Built for library-owner success</span>
                        <h1 className="mt-6 text-5xl font-bold leading-[1.08] tracking-tight">See the whole platform.<br /><span className="text-blue-400">Act on what matters.</span></h1>
                        <p className="mt-6 max-w-lg text-base leading-7 text-indigo-200">Manage owner health, subscriptions, revenue, support and public content from one calm operating workspace.</p>
                        <div className="mt-10 grid gap-4 sm:grid-cols-2">
                            {['Owner health and renewals', 'Secure billing controls', 'Audited staff actions', 'Live platform insights'].map((item) => (
                                <div key={item} className="flex items-center gap-2 text-sm text-indigo-100">
                                    <CheckCircle2 className="h-4 w-4 text-blue-400" />{item}
                                </div>
                            ))}
                        </div>
                    </div>
                    <p className="text-xs text-indigo-400">VERIVES Technologies · Internal access only</p>
                </div>
            </section>

            <section className="flex min-h-screen items-center justify-center px-5 py-10 sm:px-10">
              <div className="w-full max-w-md space-y-7 rounded-[28px] border border-gray-200 bg-white p-7 shadow-lift sm:p-9">
                <div>
                    <div className="mb-7 lg:hidden"><BrandLogo width={208} height={66} /></div>
                    <p className="admin-kicker">Secure workspace</p>
                    <h2 className="mt-2 text-3xl font-bold tracking-tight text-indigo-950">Welcome back</h2>
                    <p className="mt-2 text-sm leading-6 text-gray-500">Sign in with an authorised LibraryDesk operations account.</p>
                </div>

                {error && (
                    <div className="text-red-500 text-sm text-center bg-red-50 py-3 px-4 rounded-lg border border-red-200">
                        {error}
                    </div>
                )}

                <button
                    type="button"
                    disabled={loading}
                    onClick={handleGoogleSignIn}
                    className="group flex w-full items-center justify-center gap-3 rounded-xl border border-gray-300 bg-white px-6 py-3.5 shadow-sm transition hover:border-gray-400 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {loading ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    ) : (
                        <svg className="h-6 w-6" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                        </svg>
                    )}
                    <span className="text-gray-800 font-semibold text-base">
                        {loading ? 'Authenticating...' : 'Sign in with Google'}
                    </span>
                    {!loading && <ArrowRight className="h-4 w-4 text-gray-400 transition group-hover:translate-x-0.5" />}
                </button>

                {LOCAL_QA_LOGIN_ENABLED && (
                    <div className="pt-2 space-y-3">
                        <div className="flex items-center gap-3" aria-hidden="true">
                            <span className="h-px flex-1 bg-gray-300" />
                            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Local testing</span>
                            <span className="h-px flex-1 bg-gray-300" />
                        </div>
                        <button
                            type="button"
                            disabled={loading}
                            onClick={handleLocalSignIn}
                            className="w-full rounded-xl bg-indigo-950 px-6 py-3.5 text-base font-semibold text-white shadow-lg transition hover:bg-indigo-900 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {loading ? 'Opening QA console...' : 'Continue with dummy admin'}
                        </button>
                        <p className="text-center text-xs text-amber-700">
                            Disposable local data only. This option is unavailable on the deployed console.
                        </p>
                    </div>
                )}

                <div className="flex items-center justify-center gap-2 rounded-xl bg-gray-50 px-3 py-2.5 text-center text-xs text-gray-500">
                    <ShieldCheck className="h-4 w-4 text-emerald-600" />
                    Only authorised team members can access this system
                </div>
              </div>
            </section>
        </div>
    );
};

const ProtectedRoute = ({ children, userRole, requiresSuperAdmin }) => {
    if (requiresSuperAdmin && userRole !== 'super_admin') {
        return <Navigate to="/" replace />;
    }
    return children;
};

const App = () => {
    const [user, setUser] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = useCallback(async () => {
        const auth = getAuth();
        await signOut(auth);
        localStorage.removeItem('adminAccessToken');
        localStorage.removeItem(LOCAL_QA_SESSION_KEY);
        setUser(null);
        setUserRole(null);
    }, []);

    const handleSignIn = useCallback(async (firebaseUser) => {
        try {
            const idToken = await firebaseUser.getIdToken();
            const response = await api.post('/admin/auth', {
                idToken
            });

            const { accessToken, role } = response;
            localStorage.setItem('adminAccessToken', accessToken);
            localStorage.removeItem(LOCAL_QA_SESSION_KEY);
            setUser(firebaseUser);
            setUserRole(role);
        } catch (error) {
            console.error('Backend authentication error:', error);
            handleLogout();
            throw error;
        }
    }, [handleLogout]);

    const handleLocalSignIn = async () => {
        if (!LOCAL_QA_LOGIN_ENABLED) {
            throw new Error('Local QA login is not enabled.');
        }

        const response = await api.post('/admin/local-dummy-auth', {});
        const localUser = response.user || {
            displayName: 'Local QA Super Admin',
            email: 'qa-super-admin@librarydesk.test',
        };

        localStorage.setItem('adminAccessToken', response.accessToken);
        localStorage.setItem(LOCAL_QA_SESSION_KEY, JSON.stringify(localUser));
        setUser(localUser);
        setUserRole(response.role || 'super_admin');
    };

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                await handleSignIn(currentUser);
            } else if (LOCAL_QA_LOGIN_ENABLED && localStorage.getItem('adminAccessToken')) {
                try {
                    const localUser = JSON.parse(localStorage.getItem(LOCAL_QA_SESSION_KEY));
                    if (!localUser?.localQa) throw new Error('Not a local QA session');
                    setUser(localUser);
                    setUserRole('super_admin');
                } catch (error) {
                    localStorage.removeItem('adminAccessToken');
                    localStorage.removeItem(LOCAL_QA_SESSION_KEY);
                    setUser(null);
                    setUserRole(null);
                }
            } else {
                localStorage.removeItem('adminAccessToken');
                localStorage.removeItem(LOCAL_QA_SESSION_KEY);
                setUser(null);
                setUserRole(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [handleSignIn]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (!user) {
        return <AdminSignIn onSignIn={handleSignIn} onLocalSignIn={handleLocalSignIn} />;
    }

    return (
        <Router>
            {/* One date range for the whole console. Dashboard and Reports both
                read it, so it lives above the router's outlet. */}
            <DateRangeProvider>
                <div className="admin-shell flex h-screen overflow-hidden">
                    <Sidebar user={user} userRole={userRole} onLogout={handleLogout} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
                    <div className="min-w-0 flex-1 flex flex-col overflow-hidden">
                        <TopBar userRole={userRole} onMenuClick={() => setSidebarOpen(true)} />
                        <main className="admin-content flex-1 overflow-y-auto">
                            <Suspense fallback={<PageLoader />}>
                                <Routes>
                                    <Route path="/" element={<Dashboard />} />
                                    <Route path="/library" element={<Library />} />
                                    <Route 
                                        path="/payments" 
                                        element={
                                            <ProtectedRoute userRole={userRole} requiresSuperAdmin={true}>
                                                <PaymentsInfo />
                                            </ProtectedRoute>
                                        } 
                                    />
                                    <Route 
                                        path="/payment-receipts" 
                                        element={
                                            <ProtectedRoute userRole={userRole} requiresSuperAdmin={true}>
                                                <PaymentReceipt />
                                            </ProtectedRoute>
                                        } 
                                    />
                                    <Route 
                                        path="/team" 
                                        element={
                                            <ProtectedRoute userRole={userRole} requiresSuperAdmin={true}>
                                                <TeamManagement />
                                            </ProtectedRoute>
                                        } 
                                    />
                                    <Route path="/activity" element={<ActivityLogs />} />
                                    {/* Reading these three is authorizeAdmin on the server, so they
                                        are not behind ProtectedRoute. Each one takes userRole and
                                        turns its own write controls off for a plain team member,
                                        which is the same split the API enforces. */}
                                    <Route path="/reports" element={<Reports />} />
                                    <Route path="/settings" element={<GeneralSettings userRole={userRole} />} />
                                    <Route
                                        path="/delete-requests"
                                        element={
                                            <ProtectedRoute userRole={userRole} requiresSuperAdmin={true}>
                                                <DeleteRequests />
                                            </ProtectedRoute>
                                        }
                                    />
                                    <Route path="*" element={<Navigate to="/" replace />} />
                                </Routes>
                            </Suspense>
                        </main>
                    </div>
                </div>
            </DateRangeProvider>
        </Router>
    );
};

export default App;
